import React, { useState } from "react";
import Card from "components/card";
import { FaChevronDown, FaChevronRight } from "react-icons/fa";
import { IoIosArrowRoundBack } from "react-icons/io";
import {
  createColumnHelper,
} from "@tanstack/react-table";
import "../../../assets/css/DealTracker.css";
import { Link } from "react-router-dom";

export default function TrackerTable(props) {
  const {
    loading,
    error,
    deals = {},
    sections = [],
    expandedSections = {},
    toggleSection,
    StatusBadge,
  } = props;

  const [sorting, setSorting] = useState([]);

  // Define table columns
  const columnHelper = createColumnHelper();

  const columns = [
    columnHelper.accessor("name", {
      id: "dealname",
      header: () => (
        <p className="flex items-center gap-2 pr-16 text-sm font-bold text-gray-600 sm:min-w-[120px] md:min-w-[150px] lg:w-full lg:pr-0">
          DEAL
        </p>
      ),
      cell: (info) => (
        <div className="flex items-center gap-2 pr-16 sm:min-w-[120px] md:min-w-[150px] lg:w-full lg:pr-0">
          <div className="flex w-full flex-col">
            <p className="text-base font-[500] text-gray-800 dark:text-white">
              {info.getValue()}
            </p>
          </div>
        </div>
      ),
    }),
    columnHelper.accessor("notes", {
      id: "notes",
      header: () => (
        <p className="w-full text-sm font-bold text-gray-600 sm:min-w-[120px] md:min-w-[150px] lg:min-w-[unset]">
          NOTES
        </p>
      ),
      cell: (info) => (
        <p className="w-full text-sm dark:text-white sm:min-w-[120px] md:min-w-[150px] lg:min-w-[unset]">
          {info.getValue() || ""}
        </p>
      ),
    }),
    columnHelper.accessor("customFields.Status", {
      id: "status",
      header: () => (
        <p className="text-sm font-bold text-gray-600 sm:min-w-[120px] md:min-w-[150px] lg:min-w-[unset]">
          STATUS
        </p>
      ),
      cell: (info) => {
        const status = info.getValue();
        return (
          <div className="sm:min-w-[120px] md:min-w-[150px] lg:min-w-[unset]">
            {status && (
              <div className="flex items-center gap-x-1">
                <StatusBadge task={info.row.original} />
              </div>
            )}
          </div>
        );
      },
    }),
    columnHelper.accessor("permalinkUrl", {
      id: "asanaLink",
      header: () => (
        <p className="text-sm font-bold text-gray-600">
          ACTIONS
        </p>
      ),
      cell: (info) => {
        const permalink = info.getValue();
        const taskId = info.row.original.id;
        const fallbackUrl = taskId ? `https://app.asana.com/0/${taskId}` : null;

        return (
          <div className="text-center">
            {(permalink || fallbackUrl) ? (
              <a
                href={permalink || fallbackUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-500 hover:text-blue-700 inline-flex items-center justify-center"
                title="View in Asana"
                onClick={(e) => e.stopPropagation()}
              >
                <span className="text-sm">View in Asana</span>
              </a>
            ) : (
              <span className="text-gray-400 text-sm">No link</span>
            )}
          </div>
        );
      },
    }),
  ];

  return (
    <Card extra="w-full p-4">
      <div className="flex items-center gap-2">
        <p className="text-gray-700 text-sm dark:text-white">
          <Link to={`/deals`} className="flex items-center gap-1">
            <IoIosArrowRoundBack size={20} />
            <span>Return to Deals</span>
          </Link>
        </p>
      </div>
      <h3 className="ml-6 mb-4 mt-2 text-2xl self-start font-bold text-gray-700 dark:text-white">
        Deal Tracker
      </h3>

      {loading ? (
        <div className="mt-8 p-4 text-center">
          <p>Loading deals from Asana...</p>
        </div>
      ) : error ? (
        <div className="mt-8 p-4 bg-red-50 text-red-700 rounded-lg">
          <p className="font-bold">Error loading deals</p>
          <p>{error}</p>
        </div>
      ) : (
        <div className="mt-8 overflow-x-scroll xl:overflow-x-hidden">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-white">
                {columns.map((column) => (
                  <th
                    key={column.id}
                    onClick={() => {
                      if (column.id !== "dealname") {
                        setSorting([{ id: column.id, desc: false }]);
                      }
                    }}
                    className="mb-2 cursor-pointer pb-4 pr-4 pt-4 pl-2 text-start border border-gray-200"
                  >
                    <div className="items-center justify-between text-gray-900 text-base font-semibold">
                      {column.header()}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sections && sections.length > 0 ? (
                sections.map((section) => {
                  // Only render section if it has deals
                  if (!deals[section.id] || deals[section.id].length === 0) {
                    return null;
                  }

                  return (
                    <React.Fragment key={section.id}>
                      {/* Section header row */}
                      <tr
                        className="section-header cursor-pointer hover:bg-gray-50"
                        onClick={() => toggleSection(section.id)}
                      >
                        <td colSpan={columns.length} className="py-3 px-4 border border-gray-200">
                          <div className="flex items-center">
                            {expandedSections[section.id] ? (
                              <FaChevronDown className="mr-2 text-gray-500" />
                            ) : (
                              <FaChevronRight className="mr-2 text-gray-500" />
                            )}
                            <span className="font-bold text-lg">{section.name}</span>
                          </div>
                        </td>
                      </tr>

                      {/* Section content rows - only show when expanded */}
                      {expandedSections[section.id] && deals[section.id]?.map((task, index) => (
                        <React.Fragment key={task.id}>
                          <tr className="bg-white hover:bg-gray-50 transition-colors duration-150">
                            {columns.map((column) => {
                              const getValue = () => {
                                if (column.id === "dealname") return task.name;
                                if (column.id === "closingdate") return task.dueDate;
                                if (column.id === "notes") return task.notes;
                                if (column.id === "status") return task.customFields?.Status;
                                if (column.id === "todo") return task.completed;
                                return undefined;
                              };

                              const cellContent = column.cell({
                                getValue,
                                row: { original: task }
                              });

                              return (
                                <td
                                  key={`${task.id}-${column.id}`}
                                  className="deal-tracker-cell py-3 pr-4 pl-2 border border-gray-200"
                                >
                                  {cellContent}
                                </td>
                              );
                            })}
                          </tr>
                        </React.Fragment>
                      ))}

                      {/* Add spacing after the section's content */}
                      {expandedSections[section.id] && deals[section.id]?.length > 0 && (
                        <tr className="h-4 bg-white">
                          <td colSpan={columns.length}></td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={columns.length} className="py-4 text-center">
                    No deals found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}